import React, { useCallback, useState } from 'react';
import { Badge, Button, Dropdown, Empty } from 'antd';
import styled from 'styled-components';
import Text from 'components/Text';
import moment from 'moment';
import { notificationModalRef } from 'components/NotificationModal';
import useGetNotifications from 'features/user/services/hooks/useGetNotifications';
import userApis from 'features/user/services/apis';
import toast from 'utils/toast';
import { INotification } from 'features/user/services/types/notification';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import useCountUnReadNoti from 'features/user/services/hooks/useCountUnReadNoti';
import HomeConfigureButton, { HOME_CONFIGURE_BUTTON_TYPE } from '../HomeConfigureButton';

const NotificationBell = () => {
  const { notifications, mutate } = useGetNotifications(); // call api to get notifications
  const [countUnreadNotifications, mutateCountUnreadNotifications] = useCountUnReadNoti();
  const [open, setOpen] = useState(false);
  const modalConfirm = useModalConfirmRef();

  const handleDelete = async (id: string) => {
    try {
      const res = await userApis.deleteNotification(id);
      if (res.data.data) {
        mutate();
        mutateCountUnreadNotifications();
        toast.success('Delete notification successful');
      }
    } catch (error) {
      toast.error('Delete notification failed');
    }
  };

  const onClickDeleteNotification = useCallback(
    (data: INotification) => (e: React.MouseEvent) => {
      e.stopPropagation();
      modalConfirm.current?.open('Do you like to delete this notification?', () => {
        handleDelete(data.id);
      });
    },
    []
  );

  const onOpenChange = (open: boolean) => {
    setOpen(open);
  };
  const handleClickNotification = useCallback(async (data: INotification) => {
    try {
      // close dropdown
      notificationModalRef.current?.open(data);
      setOpen(false);
      if (!data.read) {
        await userApis.readNotification(data.id);
        mutate();
        mutateCountUnreadNotifications();
      }
    } catch (error) { }
  }, []);

  const dropdownRender = () => (
    <DropdownContainer>
      <NotificationHeader>
        <Text variant='H9' color='text_3'>
          Notification ({notifications.length})
        </Text>
      </NotificationHeader>
      <Divider />
      <ListContainer>
        {notifications.length === 0 && <Empty description='No notification' />}
        {notifications.length > 0 &&
          notifications.map((item) => (
            <NotificationItem
              key={item.id}
              className={item.read ? '' : 'un_read'}
              onClick={() => handleClickNotification(item)}
            >
              <NotificationContent>
                <TitleWrapper>
                  {!item.read && <UnreadIndicator />}
                  <Text
                    variant='CONTENT_1'
                    color={item.read ? 'text_2' : 'text_3'}
                    style={{ fontWeight: item.read ? 400 : 700 }}
                  >
                    {item.title}
                  </Text>
                </TitleWrapper>
                <NotificationMeta>
                  <Text variant='CONTENT_2' color={item.read ? 'text_2' : 'v2_text_3'}>
                    {moment(item.createdDate).format('MM-DD-YYYY')}
                  </Text>
                </NotificationMeta>
              </NotificationContent>
              <DeleteButton onClick={onClickDeleteNotification(item)} type='button'>
                <IcDelete />
              </DeleteButton>
            </NotificationItem>
          ))}
      </ListContainer>
    </DropdownContainer>
  );

  return (
    <DropdownWrapper>
      <Dropdown
        open={open}
        arrow={true}
        trigger={['click']}
        onOpenChange={onOpenChange}
        placement='bottomLeft'
        overlayClassName='notification-dropdown'
        dropdownRender={dropdownRender}
      >
        <HomeConfigureButton onClick={(e) => e.preventDefault()} type={HOME_CONFIGURE_BUTTON_TYPE.NOTIFICATION} count={countUnreadNotifications} />
      </Dropdown>
      <ModalConfirm ref={modalConfirm} />
    </DropdownWrapper>
  );
};

export default NotificationBell;

const DropdownWrapper = styled.div`
  .notification-dropdown {
    .ant-dropdown-menu {
      padding: 0;
      border-radius: 0;
      box-shadow: none;
    }

    .ant-dropdown-menu-item {
      padding: 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

const DropdownContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 540px;
  max-width: 600px;
  overflow: hidden;
`;

const BellButton = styled.button``;

const NotificationHeader = styled.div`
  padding: 12px 16px;
  background: #fff;
`;

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 0;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  border: 1px solid #e9edf2;
  background: #fff;
  position: relative;

  &:hover {
    opacity: 0.8;
  }

  &.un_read {
    background-color: #F0F7FF;
    border-left: 4px solid #4384FF;
    padding-left: 12px;
    box-shadow: 0 2px 8px 0 rgba(67, 132, 255, 0.15);
  }

  &:not(.un_read) {
    background-color: #fff;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }
`;

const NotificationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotificationMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const UnreadIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4384FF;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }

  &:active {
    background-color: #e0e0e0;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  max-height: 500px;
  overflow-y: auto;
`;

const IcBell = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='34' height='40' viewBox='0 0 34 40' fill='none'>
    <path
      d='M32.5009 27.4391L30.1767 25.0711C29.8517 24.7406 29.6728 24.3031 29.6728 23.8398V16.6797C29.6728 10.9 25.8142 6.00547 20.5377 4.43516C20.5853 4.19766 20.6103 3.95469 20.6103 3.71094C20.6103 1.66484 18.9455 0 16.8994 0C14.8533 0 13.1885 1.66484 13.1885 3.71094C13.1885 3.95703 13.2127 4.19922 13.2588 4.43594C7.98376 6.00703 4.12595 10.9008 4.12595 16.6797V23.8398C4.12595 24.3031 3.94705 24.7406 3.62205 25.0711L1.29783 27.4391C0.00720114 28.7531 -0.352174 30.625 0.361108 32.3242C1.07361 34.0227 2.66111 35.0781 4.50329 35.0781H11.3541C11.6838 37.8461 14.0439 40 16.8994 40C19.7549 40 22.115 37.8461 22.4447 35.0781H29.2955C31.1377 35.0781 32.7252 34.0227 33.4377 32.3242C34.151 30.625 33.7916 28.7531 32.5009 27.4391ZM15.9228 3.71094C15.9228 3.17266 16.3611 2.73438 16.8994 2.73438C17.4377 2.73438 17.876 3.17266 17.876 3.71094C17.876 3.79531 17.865 3.87109 17.8478 3.94141C17.5345 3.91875 17.2181 3.90625 16.8994 3.90625C16.5806 3.90625 16.2642 3.91875 15.951 3.94141C15.9338 3.87188 15.9228 3.79531 15.9228 3.71094ZM16.8994 37.2656C15.5556 37.2656 14.4267 36.3312 14.1267 35.0781H19.672C19.372 36.3312 18.2431 37.2656 16.8994 37.2656ZM30.9166 31.2664C30.8103 31.5195 30.3799 32.3438 29.2955 32.3438H4.50329C3.41892 32.3438 2.98845 31.5187 2.8822 31.2664C2.77595 31.0133 2.48923 30.1289 3.24861 29.3547L5.57361 26.9867C6.4033 26.1414 6.86033 25.0242 6.86033 23.8398V16.6797C6.86033 11.1445 11.3635 6.64062 16.8994 6.64062C22.4353 6.64062 26.9384 11.1445 26.9384 16.6797V23.8398C26.9384 25.0242 27.3955 26.1414 28.2252 26.9867L30.5502 29.3547C31.3095 30.1281 31.0228 31.0133 30.9166 31.2664Z'
      fill='#232F3E'
    />
  </svg>
);

const IcDelete = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='15' height='23' viewBox='0 0 15 23' fill='none'>
    <path
      d='M14.0283 8.01221C14.0204 8.07668 14.0119 8.14243 14.0039 8.21045L13.8584 9.41943L13.8564 9.44092C13.8538 9.47565 13.8492 9.51126 13.8438 9.55615C13.8392 9.59295 13.8336 9.63953 13.8291 9.68799C13.7053 10.6999 13.5816 11.7138 13.4648 12.728C13.341 13.7408 13.2247 14.7461 13.1084 15.7495C13.0429 16.3029 12.9776 16.8563 12.9121 17.4097C12.8466 17.9632 12.7804 18.5173 12.7148 19.0708V19.0718C12.5974 20.092 12.4764 21.0734 12.3584 22.0913C12.3523 22.141 12.3425 22.1667 12.3369 22.1782C12.3254 22.1825 12.2979 22.1909 12.2432 22.1909H10.4121V22.0229H10.2041C10.1991 22.02 10.1945 22.016 10.1895 22.0132C9.98526 21.8998 9.74714 21.9188 9.56348 22.0356L9.55469 22.0415C9.49241 22.083 9.45055 22.13 9.4248 22.1626C9.42144 22.1669 9.41913 22.1721 9.41602 22.1763H5.1543V21.7065L4.70508 21.9312C4.70311 21.931 4.70119 21.9303 4.69922 21.9302L4.57715 21.9312C4.50381 21.9389 4.31545 21.968 4.1709 22.1343C4.15914 22.1478 4.14965 22.1625 4.13965 22.1763H2.46875C2.43886 22.1763 2.42142 22.1711 2.41406 22.1685L2.40625 22.1646C2.40591 22.1642 2.40511 22.1637 2.4043 22.1626C2.40313 22.161 2.40085 22.158 2.39844 22.1528L2.38281 22.0894L2.30273 21.3901C2.18523 20.428 2.07811 19.4898 1.95996 18.5151C1.90907 18.0825 1.85851 17.6499 1.80762 17.2173L1.6543 15.9175C1.61412 15.5669 1.57207 15.2166 1.53027 14.8677C1.48837 14.5179 1.44711 14.1689 1.40723 13.8208L1.40625 13.8169L1.09375 11.1841C1.0468 10.7694 1.00168 10.4247 0.956055 10.0142L0.955078 10.0103L0.80957 8.77979C0.796168 8.66586 0.782934 8.55191 0.769531 8.43799V8.40771L0.765625 8.37842C0.751101 8.25642 0.737156 8.13447 0.722656 8.01221H14.0283ZM11.2725 8.37939C10.646 8.2899 10.067 8.71485 9.96484 9.34033L9.96387 9.34521C9.95475 9.40457 9.94859 9.46419 9.94336 9.51123C9.93768 9.56238 9.93314 9.60108 9.92773 9.63623L9.92578 9.64795C9.7872 10.7129 9.65648 11.7702 9.52539 12.8335L9.52148 12.8638V12.895C9.52134 12.897 9.52118 12.9009 9.52051 12.9067C9.51915 12.9185 9.51664 12.9328 9.51367 12.9507L9.51172 12.9614L9.51074 12.9712L9.40137 13.8442C9.343 14.3003 9.28687 14.7545 9.23047 15.2075C9.17401 15.661 9.1177 16.1137 9.05957 16.5679V16.5688C8.99037 17.1225 8.92075 17.6786 8.85156 18.2339C8.78274 18.7863 8.71432 19.3387 8.64551 19.8892C8.61758 20.0849 8.60651 20.3398 8.72754 20.6089L8.73242 20.6177C8.9574 21.0926 9.45697 21.3643 9.9873 21.2759L9.99316 21.2749C10.5056 21.1829 10.8868 20.7553 10.9473 20.2349C11.0054 19.7807 11.062 19.3264 11.1182 18.8735C11.1746 18.4183 11.231 17.9642 11.2891 17.5103L11.29 17.5073C11.3481 17.0316 11.4077 16.5556 11.4678 16.0786C11.5278 15.6023 11.5882 15.1245 11.6465 14.647L11.9746 12.0474C12.0764 11.2479 12.1785 10.4521 12.2734 9.64893C12.3505 9.01883 11.9022 8.45449 11.2715 8.38037L11.2725 8.37939ZM3.44238 8.37158L3.43945 8.37256C2.79816 8.46432 2.37792 9.06687 2.46094 9.68701V9.68799L2.69336 11.5552V11.5562C2.80257 12.4589 2.91247 13.3646 3.0293 14.27C3.1532 15.2321 3.27634 16.1841 3.39258 17.1431L3.39355 17.146C3.52459 18.1797 3.65506 19.2209 3.78613 20.2622L3.78711 20.271C3.8733 20.873 4.40049 21.3332 5.03027 21.2808C5.6299 21.2308 6.11883 20.7333 6.09277 20.0923L6.0918 20.061L6.08691 20.0298L6.08496 20.0054L6.07422 19.896C6.01624 19.4215 5.95611 18.9454 5.89648 18.4702C5.83638 17.9912 5.77592 17.5127 5.71777 17.0366V17.0356L5.3252 13.8745C5.27423 13.4631 5.22284 13.0515 5.17188 12.6401C5.12105 12.2299 5.06938 11.8199 5.01855 11.4097H5.01953C4.93902 10.7216 4.85101 10.0406 4.76367 9.36377V9.35986L4.74023 9.23975C4.59747 8.65042 4.03286 8.29055 3.44238 8.37158ZM8.52637 9.40869L8.52441 9.38818C8.45994 8.57891 7.5915 8.07982 6.8584 8.43994C6.64989 8.53989 6.47735 8.68971 6.36035 8.88916C6.24405 9.08755 6.20025 9.30524 6.2002 9.51514V20.0542C6.2002 20.1012 6.20098 20.187 6.20996 20.2739L6.20898 20.2749C6.26183 20.9487 6.90554 21.4265 7.55957 21.311L7.56445 21.3101C7.84466 21.2575 8.09579 21.1185 8.27344 20.8931C8.44898 20.67 8.52637 20.397 8.52637 20.1196V9.40869Z'
      fill='#D2464F'
      stroke='#D2464F'
    />
    <path
      d='M0.371439 5.96872C1.82078 5.5463 3.27012 5.12388 4.71218 4.70146L4.75588 4.68689C7.23942 3.95858 9.7084 3.23755 12.1847 2.51652L12.4396 2.44369C12.4469 2.44369 12.4541 2.44369 12.4614 2.44369V2.43641L12.6144 2.39271L12.811 2.33444C12.811 2.33444 12.8183 2.33444 12.8183 2.34173L12.8256 2.33444L13.2043 2.2252C13.2116 2.2252 13.2189 2.2252 13.2189 2.2252C13.2189 2.2252 13.2189 2.21791 13.2261 2.21791L13.3937 2.16693C13.5175 2.13052 13.6413 2.0941 13.7578 2.05768H13.7651C13.8452 2.03584 13.9253 2.0067 14.0127 1.98485C13.9035 1.59885 13.7869 1.22741 13.685 0.855969C13.5903 0.513663 13.2917 0.353434 12.9567 0.448115C11.9152 0.754006 10.881 1.0599 9.8395 1.3585C9.71568 1.39492 9.59187 1.43134 9.46806 1.46047C9.45349 1.42405 9.44621 1.39492 9.43893 1.37307C9.33696 1.03076 9.24228 0.688458 9.14032 0.353434C9.06749 0.113091 8.85628 -0.0252884 8.60137 0.00384413C8.53582 0.0111272 8.47027 0.0329766 8.40472 0.054826C6.91168 0.491813 5.43321 0.921518 3.95473 1.35122C3.65613 1.43862 3.52503 1.69353 3.61243 1.99214C3.70711 2.32716 3.80907 2.66218 3.90375 2.99721C3.9256 3.06276 3.91104 3.09189 3.84549 3.10645C3.4959 3.20842 3.13903 3.31038 2.78944 3.41235C2.00286 3.64541 1.21628 3.87846 0.422421 4.10424C0.276759 4.14794 0.160229 4.22077 0.0873975 4.35187C0.0509819 4.41742 0.0291325 4.49753 0 4.56308C0 4.60678 0 4.65776 0 4.70146C0.0655481 4.91995 0.131096 5.13844 0.196644 5.36422C0.254909 5.56815 0.313174 5.76479 0.371439 5.96872ZM8.18623 1.25654C8.23721 1.43862 8.28819 1.60613 8.33917 1.78821C8.31004 1.80278 8.28091 1.81006 8.25178 1.81734C7.98958 1.89746 7.72011 1.97757 7.45792 2.0504C7.45063 2.0504 7.44335 2.05768 7.43607 2.05768L6.61307 2.29803C6.08869 2.45097 5.5643 2.60392 5.03264 2.75686C4.98166 2.57479 4.93067 2.40727 4.87241 2.21791C5.97944 1.89746 7.07919 1.577 8.18623 1.25654Z'
      fill='#D2464F'
    />
  </svg>
);
